
import { defineComponent, reactive, ref, watch, watchEffect } from 'vue';
import Alert from '@/components/ui-elements/Alert.vue';
import BudgetTemplateTable from '@/components/tables/BudgetTemplateTable.vue';
import Button from '@/components/ui-elements/form/Button.vue';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';
import ExpenseModal from '@/components/modals/ExpenseModal.vue';
import ExpenseSlideover from '@/components/slideovers/ExpenseSlideover.vue';
import SaveIcon from '@/components/ui-elements/icons/SaveIcon.vue';
import { useTemplateStore, useTypesStore } from '@/store';
import useTimestamp from '@/hooks/useTimestamp';
import { BudgetExpense } from '@/store/budget';
import useRemoveExpense from '@/hooks/useRemoveExpense';

export default defineComponent({
	components: {
		Alert,
		BudgetTemplateTable,
		Button,
		ConfirmationModal,
		ExpenseModal,
		ExpenseSlideover,
		SaveIcon,
	},
	setup() {
		const { generateTempId } = useTimestamp();
		const templateStore = useTemplateStore();
		const typeStore = useTypesStore();
		const {
			getRemoveExpenseList,
			removeExpense,
			resetList,
			setItemToBeRemoved,
		} = useRemoveExpense();

		const alert = reactive({
			hide: true,
			show: false,
			message: '',
			type: '',
		});
		const showConfirmModal = ref(false);
		const disableSave = ref(true);
		const expenses = ref<Record<string, BudgetExpense[]>>(
			{} as Record<string, BudgetExpense[]>
		);
		const expenseData = ref({});
		const selectedCategory = ref('');
		const showModal = ref(false);

		watch(showModal, n => {
			if (!n) {
				expenseData.value = {};
			}
		});

		watchEffect(() => {
			expenses.value = JSON.parse(JSON.stringify(templateStore.expenses));
		});

		const confirmRemoveExpense = () => {
			const data = removeExpense(
				expenses.value as Record<string, Array<BudgetExpense>>
			);
			expenses.value = data.expenses;
			disableSave.value = !data.save;
		};

		const openExpense = (data: {
			category: string;
			expense?: BudgetExpense;
		}) => {
			if (data.expense) {
				expenseData.value = data.expense;
			}
			selectedCategory.value = data.category;
			showModal.value = true;
		};

		const saveBudgetTemplate = async () => {
			disableSave.value = true;
			const rmResponse = await templateStore.removeExpense(
				getRemoveExpenseList()
			);

			alert.type = 'error';
			alert.message = 'There was a problem saving the template.';

			if (rmResponse.success) {
				const response = await templateStore.saveTemplate(
					expenses.value
				);

				if (response.success) {
					alert.type = 'success';
					alert.message = 'Template was saved successfully';
					resetList();
				}
			}

			alert.hide = false;
			setTimeout(() => {
				alert.show = true;
				setTimeout(() => {
					alert.show = false;
					setTimeout(() => (alert.hide = true), 275);
				}, 6000);
			}, 75);
		};

		const setDeleteAndShowConfirmation = (e: {
			id: string | number;
			category: string;
		}) => {
			setItemToBeRemoved(e);
			showConfirmModal.value = true;
		};

		const updateLocalExpense = (modalData: {
			type: string;
			data: BudgetExpense;
		}) => {
			const { data } = modalData;
			const index = expenses.value[selectedCategory.value].findIndex(
				(expense: BudgetExpense) => expense.id === (data.id ?? -1)
			);
			disableSave.value = false;

			if (index > -1) {
				expenses.value[selectedCategory.value][index] = data;
			} else {
				const result: BudgetExpense = {
					...data,
					id: generateTempId(),
					[typeStore.getTypeColumnNameFromType(
						selectedCategory.value
					)]: (data as any).type,
				};

				delete (result as any).type;
				expenses.value[selectedCategory.value].push(result);
			}
		};

		return {
			alert,
			confirmRemoveExpense,
			disableSave,
			expenses,
			expenseData,
			openExpense,
			saveBudgetTemplate,
			selectedCategory,
			setDeleteAndShowConfirmation,
			showConfirmModal,
			showModal,
			updateLocalExpense,
		};
	},
});
